/* eslint-disable i18next/no-literal-string */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useMutation } from 'react-query';
import {
  Box, Typography, Alert, CircularProgress,
  Tooltip, Drawer, Divider, List, ListItem,
} from '@mui/material';
import { Button, Dialog, TextField } from 'Components/ui';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useAuthTenant } from 'store/auth';
import { createScannerAccount, getScannerAccountConnection } from 'services/scanner-service';

// ── Constants ─────────────────────────────────────────────────────────────────
const VO_AWS_ACCOUNT_ID = '123456789012'; // Replace with real VO AWS account ID
const VO_AWS_ACCOUNT_ARN = `arn:aws:iam::${VO_AWS_ACCOUNT_ID}:root`;

function generateExternalId() {
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
  );
}

// ── Sidebar steps ─────────────────────────────────────────────────────────────
const SIDEBAR_STEPS = [
  { label: 'Link a Provider',          desc: 'Enter the provider details you would like to add.' },
  { label: 'Authenticate Credentials', desc: 'Autorize uma conexão segura entre o Virtual Officer e seu provedor.' },
  { label: 'Validate Connection',      desc: 'Review provider resources and test the connection.' },
  { label: 'Launch Scan',              desc: 'Scan newly connected resources.' },
];

// ── Provider catalogue ────────────────────────────────────────────────────────
const PROVIDERS = [
  { id: 'aws',        label: 'Amazon Web Services',  initials: 'AWS',  color: '#FF9900', textColor: 'var(--vo-white)', available: true },
  { id: 'gcp',        label: 'Google Cloud Platform', initials: 'GCP',  color: '#4285F4', textColor: 'var(--vo-white)', available: true },
  { id: 'azure',      label: 'Microsoft Azure',       initials: 'AZ',   color: '#0078D4', textColor: 'var(--vo-white)', available: true },
  { id: 'm365',       label: 'Microsoft 365',         initials: 'M365', color: '#D83B01', textColor: 'var(--vo-white)', available: false },
  { id: 'mongodb',    label: 'MongoDB Atlas',          initials: 'MDB',  color: '#00ED64', textColor: 'var(--vo-ink)', available: true },
  { id: 'kubernetes', label: 'Kubernetes',             initials: 'K8s',  color: '#326CE5', textColor: 'var(--vo-white)', available: false },
  { id: 'github',     label: 'GitHub',                initials: 'GH',   color: '#24292E', textColor: 'var(--vo-white)', available: false },
];

// ── Shared helpers ─────────────────────────────────────────────────────────────
const BTN = {
  borderRadius: '8px', padding: '8px 20px', height: '36px',
  textTransform: 'none', fontSize: '0.875rem', fontWeight: 500,
};

const T = {
  bg: 'var(--vo-mist)',
  border: 'var(--vo-line)',
  text: 'var(--vo-ink)',
  muted: 'var(--vo-slate)',
  dim: 'var(--vo-steel)',
};

function copy(text) {
  navigator.clipboard.writeText(text);
  Dialog.fire({ toast: true, icon: 'success', title: 'Copiado!', position: 'top-end', showConfirmButton: false, timer: 1200 });
}

const CopyBox = ({ value }) => (
  <Box sx={{
    display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: 1.5,
    background: 'var(--vo-mist)', border: `1px solid ${T.border}`, borderRadius: 1.5,
    p: '10px 14px', mb: 1.5,
  }}>
    <Typography sx={{ fontSize: 13, fontFamily: 'monospace', color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {value}
    </Typography>
    <Tooltip title={value}>
      <Button size="sm" variant="secondary" onClick={() => copy(value)}
        sx={{ flexShrink: 0, minWidth: 36, height: 30, px: 1, borderRadius: '6px', borderColor: T.border }}>
        <ContentCopyIcon sx={{ fontSize: 15 }} />
      </Button>
    </Tooltip>
  </Box>
);

// ── Docs content per provider ─────────────────────────────────────────────────
const DOCS = {
  aws: {
    title: 'Amazon Web Services — Configuração',
    color: '#FF9900',
    initials: 'AWS',
    steps: [
      {
        heading: '1. Acesse o console IAM da AWS',
        body: 'Navegue até Services → Security, Identity & Compliance → IAM → Roles → Create Role.',
      },
      {
        heading: '2. Selecione "Another AWS account" como entidade confiável',
        body: 'Insira o Account ID do Virtual Officer exibido no formulário ao lado e marque a opção "Require external ID". Cole o ExternalId exibido no formulário.',
      },
      {
        heading: '3. Anexe a política gerenciada SecurityAudit',
        body: 'Na etapa de permissões, pesquise e selecione a policy "SecurityAudit" (gerenciada pela AWS). Ela concede acesso de leitura a todos os recursos necessários para a varredura.',
      },
      {
        heading: '4. Nomeie e crie a role',
        body: 'Dê um nome à role (ex: "VirtualOfficerScanRole") e confirme a criação.',
      },
      {
        heading: '5. Copie o Role ARN',
        body: 'Após criar a role, clique nela e copie o campo "ARN" (ex: arn:aws:iam::123456789012:role/VirtualOfficerScanRole). Cole no formulário.',
      },
    ],
    tip: 'A role precisa ter exatamente o ExternalId gerado pelo Virtual Officer na política de confiança. Isso garante que só o Virtual Officer pode assumir a role.',
  },
  gcp: {
    title: 'Google Cloud Platform — Configuração',
    color: '#4285F4',
    initials: 'GCP',
    steps: [
      {
        heading: '1. Acesse o Console GCP',
        body: 'Navegue até IAM & Admin → Service Accounts → Create Service Account.',
      },
      {
        heading: '2. Crie a service account',
        body: 'Dê um nome (ex: "vo-scanner") e clique em Create and Continue.',
      },
      {
        heading: '3. Atribua a role Security Reviewer',
        body: 'No campo "Role", pesquise e selecione "Security Reviewer". Clique em Continue → Done.',
      },
      {
        heading: '4. Gere a chave JSON',
        body: 'Abra a service account criada → Keys → Add Key → Create New Key → JSON. Um arquivo será baixado automaticamente.',
      },
      {
        heading: '5. Cole o conteúdo do JSON no formulário',
        body: 'Abra o arquivo .json em um editor de texto e cole todo o conteúdo no campo "Service Account JSON" do formulário.',
      },
    ],
    tip: 'O arquivo JSON contém credenciais sensíveis. Não o compartilhe ou versione em repositórios de código.',
  },
  azure: {
    title: 'Microsoft Azure — Configuração',
    color: '#0078D4',
    initials: 'AZ',
    steps: [
      {
        heading: '1. Registre um aplicativo no Azure AD',
        body: 'Acesse Azure Active Directory → App registrations → New registration. Dê um nome (ex: "VirtualOfficerScanner") e registre.',
      },
      {
        heading: '2. Anote o Tenant ID e o Client ID',
        body: 'Na página do app registrado, você encontra o "Directory (tenant) ID" e o "Application (client) ID". Esses valores vão para o formulário.',
      },
      {
        heading: '3. Crie um Client Secret',
        body: 'Acesse Certificates & secrets → New client secret. Defina a validade e copie o valor gerado imediatamente (ele não será exibido novamente).',
      },
      {
        heading: '4. Atribua a role Reader na assinatura',
        body: 'Acesse Subscriptions → [sua assinatura] → Access control (IAM) → Add role assignment. Selecione "Reader" e atribua ao aplicativo registrado.',
      },
    ],
    tip: 'O Reader role permite leitura de todos os recursos sem alterar nada. Para cobertura de segurança completa, adicione também Security Reader em Microsoft Defender for Cloud.',
  },
  mongodb: {
    title: 'MongoDB Atlas — Configuração',
    color: '#00ED64',
    initials: 'MDB',
    textColor: 'var(--vo-ink)',
    steps: [
      {
        heading: '1. Encontre o Organization ID',
        body: 'No MongoDB Atlas, acesse o menu do canto superior esquerdo → clique no nome da organização → Settings. O campo "Organization ID" está no topo da página.',
      },
      {
        heading: '2. Crie uma chave de API',
        body: 'Ainda em Settings, acesse Access Manager → API Keys → Create API Key.',
      },
      {
        heading: '3. Defina a permissão Organization Read Only',
        body: 'No campo "Organization Permissions", selecione "Organization Read Only". Essa permissão é suficiente para a varredura de segurança.',
      },
      {
        heading: '4. Copie Public Key e Private Key',
        body: 'Clique em Next. O Public Key é exibido na tela e o Private Key é mostrado apenas uma vez. Copie ambos antes de fechar.',
      },
      {
        heading: '5. Configure o acesso à API (API Access List)',
        body: 'Adicione o endereço IP do Virtual Officer ou use 0.0.0.0/0 para liberar qualquer origem (menos seguro).',
      },
    ],
    tip: 'A Private Key é exibida somente no momento da criação. Guarde-a em local seguro antes de fechar a janela do Atlas.',
  },
};

const Step = ({ heading, body }) => (
  <ListItem alignItems="flex-start" sx={{ px: 0, py: 1.5, display: 'block' }}>
    <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--vo-ink)', mb: 0.5 }}>{heading}</Typography>
    <Typography sx={{ fontSize: 12.5, color: 'var(--vo-ink-2)', lineHeight: 1.6 }}>{body}</Typography>
  </ListItem>
);

const DocsDrawer = ({ open, onClose, provider }) => {
  const doc = DOCS[provider];
  if (!doc) return null;
  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: 400, p: 0, display: 'flex', flexDirection: 'column' } }}>
      {/* Header */}
      <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--vo-line)', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: 1, backgroundColor: doc.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: doc.textColor || 'var(--vo-white)', lineHeight: 1 }}>{doc.initials}</Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--vo-ink)' }}>{doc.title}</Typography>
            <Typography sx={{ fontSize: 11, color: 'var(--vo-steel)' }}>Guia de integração</Typography>
          </Box>
        </Box>
        <Button size="sm" variant="text" onClick={onClose} sx={{ minWidth: 32, p: 0.5, color: 'var(--vo-steel)' }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </Button>
      </Box>

      {/* Steps */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
        <List disablePadding>
          {doc.steps.map((s, i) => (
            <Box key={i}>
              <Step heading={s.heading} body={s.body} />
              {i < doc.steps.length - 1 && <Divider />}
            </Box>
          ))}
        </List>

        {doc.tip && (
          <Box sx={{ mt: 2, p: 2, borderRadius: 1.5, backgroundColor: 'var(--vo-warning-10)', border: '1px solid var(--vo-warning-15)' }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--vo-warning)', mb: 0.5 }}>💡 Dica</Typography>
            <Typography sx={{ fontSize: 12, color: 'var(--vo-ink-2)', lineHeight: 1.6 }}>{doc.tip}</Typography>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

// ── Sidebar ───────────────────────────────────────────────────────────────────
const Sidebar = ({ currentStep }) => {
  const sidebarIdx = currentStep === 0 ? 0 : 1;

  return (
    <Box sx={{
      width: 240, flexShrink: 0,
      backgroundColor: 'var(--vo-mist)',
      borderRight: `1px solid ${T.border}`,
      p: 3, display: 'flex', flexDirection: 'column',
    }}>
      {SIDEBAR_STEPS.map((s, i) => {
        const done   = i < sidebarIdx;
        const active = i === sidebarIdx;
        return (
          <Box key={s.label} sx={{
            display: 'flex', gap: 1.5, py: 2,
            borderBottom: i < SIDEBAR_STEPS.length - 1 ? `1px solid ${T.border}` : 'none',
          }}>
            <Box sx={{ mt: 0.3, flexShrink: 0, color: done ? 'var(--vo-success)' : active ? 'var(--vo-celeste)' : 'var(--vo-steel)' }}>
              {done
                ? <CheckCircleIcon sx={{ fontSize: 18 }} />
                : <RadioButtonUncheckedIcon sx={{ fontSize: 18 }} />}
            </Box>
            <Box>
              <Typography sx={{
                fontSize: 13, fontWeight: active ? 600 : 400,
                color: active ? T.text : done ? 'var(--vo-ink-2)' : T.dim,
                lineHeight: 1.3,
              }}>
                {s.label}
              </Typography>
              <Typography sx={{
                fontSize: 11, mt: 0.4, lineHeight: 1.4,
                color: T.muted,
                display: active ? 'block' : 'none',
              }}>
                {s.desc}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
export const ScannerAccountNew = () => {
  const navigate = useNavigate();
  const selectedTenant = useSelector(useAuthTenant);

  // Wizard state
  const [step, setStep]                     = useState(0);  // 0 = select provider, 1+ = provider-specific
  const [provider, setProvider]             = useState(null);
  const [awsMethod, setAwsMethod]           = useState(null); // 'role' | 'creds'
  const [name, setName]                     = useState('');
  const [accountId, setAccountId]           = useState('');
  const [alias, setAlias]                   = useState('');
  const [roleArn, setRoleArn]               = useState('');
  const [externalId]                        = useState(generateExternalId);
  const [azureTenantId, setAzureTenantId]   = useState('');
  const [azureClientId, setAzureClientId]   = useState('');
  const [azureClientSecret, setAzureClientSecret] = useState('');
  const [gcpJson, setGcpJson]               = useState('');
  const [mongoOrgId, setMongoOrgId]           = useState('');
  const [mongoPublicKey, setMongoPublicKey]   = useState('');
  const [mongoPrivateKey, setMongoPrivateKey] = useState('');
  const [awsAccessKey, setAwsAccessKey]       = useState('');
  const [awsSecretKey, setAwsSecretKey]       = useState('');
  const [awsSessionToken, setAwsSessionToken] = useState('');
  const [showSecret, setShowSecret]           = useState(false);
  const [docsOpen, setDocsOpen]               = useState(false);

  // ── Connection validation state ──────────────────────────────────────────────
  const [validating, setValidating]           = useState(false);
  const [connStatus, setConnStatus]           = useState(null); // null | 'pending' | 'connected' | 'failed'
  const pollRef = useRef(null);

  const iamTrustPolicy = JSON.stringify({
    Version: '2012-10-17',
    Statement: [{
      Effect: 'Allow',
      Principal: { AWS: VO_AWS_ACCOUNT_ARN },
      Action: 'sts:AssumeRole',
      Condition: { StringEquals: { 'sts:ExternalId': externalId } },
    }],
  }, null, 2);

  // ── Stop polling on unmount ──────────────────────────────────────────────────
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const startConnectionPolling = (accountId, slug) => {
    setValidating(true);
    setConnStatus('pending');

    const POLL_INTERVAL = 4000;
    const TIMEOUT_MS = 60000;
    const startedAt = Date.now();

    pollRef.current = setInterval(async () => {
      try {
        const res = await getScannerAccountConnection(accountId, slug);
        const { status } = res.data ?? {};
        if (status === 'connected' || status === 'failed') {
          clearInterval(pollRef.current);
          setConnStatus(status);
        } else if (Date.now() - startedAt > TIMEOUT_MS) {
          clearInterval(pollRef.current);
          setConnStatus('failed');
        }
      } catch {
        // keep polling
      }
    }, POLL_INTERVAL);
  };

  const mutation = useMutation(
    (payload) => createScannerAccount(payload),
    {
      onSuccess: (res) => {
        const { validationError, _id } = res.data ?? {};
        if (validationError) {
          Dialog.fire({
            icon: 'warning',
            title: 'Conta criada com aviso',
            html: `<p>${validationError}</p>
              <p>Verifique se o Role ARN está correto e a política de confiança inclui:</p>
              <ul style="text-align:left">
                <li>Principal: <code>${VO_AWS_ACCOUNT_ARN}</code></li>
                <li>Condition ExternalId: <code>${externalId}</code></li>
              </ul>`,
          });
          navigate('/scanner/accounts');
        } else if (_id) {
          startConnectionPolling(_id, selectedTenant?.slug);
        } else {
          navigate('/scanner/accounts');
        }
      },
      onError: (err) =>
        Dialog.fire({
          title: 'Erro',
          text: err?.response?.data?.message || 'Não foi possível cadastrar.',
          icon: 'error',
        }),
    }
  );

  const handleSubmit = () => {
    const effectiveName = name || alias || accountId || 'Conta ' + (provider ?? '').toUpperCase();
    const payload = { name: effectiveName, provider, companyKey: selectedTenant.slug };
    if (provider === 'aws') {
      if (awsMethod === 'credentials') {
        payload.aws = { method: 'static', accountId, accessKeyId: awsAccessKey, secretAccessKey: awsSecretKey, ...(awsSessionToken ? { sessionToken: awsSessionToken } : {}) };
      } else {
        payload.aws = { roleArn, externalId };
      }
    } else if (provider === 'azure') {
      payload.azure = { tenantId: azureTenantId, clientId: azureClientId, clientSecret: azureClientSecret };
    } else if (provider === 'gcp') {
      payload.gcp = { serviceAccountJson: gcpJson };
    } else if (provider === 'mongodb') {
      payload.mongodb = { organizationId: mongoOrgId, publicApiKey: mongoPublicKey, privateApiKey: mongoPrivateKey };
    }
    mutation.mutate(payload);
  };

  // ── Next / Back ──────────────────────────────────────────────────────────
  const canGoNext = () => {
    if (step === 0) return !!provider;
    if (provider === 'aws') {
      if (step === 1) return !!accountId;   // Account ID entered
      if (step === 2) return !!awsMethod;   // Auth method chosen
      if (step === 3) {
        if (awsMethod === 'role') return !!roleArn;
        if (awsMethod === 'credentials') return !!awsAccessKey && !!awsSecretKey;
        return false;
      }
    }
    if (provider === 'azure') {
      if (step === 1) return !!azureTenantId && !!azureClientId && !!azureClientSecret;
    }
    if (provider === 'gcp') {
      if (step === 1) return !!gcpJson;
    }
    if (provider === 'mongodb') {
      if (step === 1) return !!mongoOrgId && !!mongoPublicKey && !!mongoPrivateKey;
    }
    return true;
  };

  const goNext = () => setStep(s => s + 1);
  const goBack = () => {
    if (step === 0) navigate('/scanner/accounts');
    else setStep(s => s - 1);
  };

  // Final step differs per provider
  const isFinalStep = () => {
    if (provider === 'aws')     return step === 3;
    if (provider === 'azure')   return step === 2;
    if (provider === 'gcp')     return step === 2;
    if (provider === 'mongodb') return step === 2;
    return false;
  };

  // ── Docs label per provider ───────────────────────────────────────────────
  const docsLabel = provider === 'aws' ? 'Guia AWS' : provider === 'azure' ? 'Guia Azure' : provider === 'gcp' ? 'Guia GCP' : provider === 'mongodb' ? 'Guia MongoDB Atlas' : 'Documentação';

  // ── Connection validation screen ─────────────────────────────────────────
  if (validating) {
    const isPending   = connStatus === 'pending';
    const isConnected = connStatus === 'connected';
    const isFailed    = connStatus === 'failed';
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)', backgroundColor: 'var(--vo-white)', overflow: 'hidden' }}>
        <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: T.text }}>Validando Conexão</Typography>
        </Box>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, p: 4 }}>
          {isPending && (
            <>
              <CircularProgress size={56} sx={{ color: 'var(--vo-celeste)' }} />
              <Typography sx={{ fontSize: 16, fontWeight: 600, color: T.text }}>Verificando credenciais…</Typography>
              <Typography sx={{ fontSize: 13, color: T.muted, textAlign: 'center', maxWidth: 400 }}>
                O Prowler está testando a conexão com o seu provedor cloud. Isso pode levar alguns segundos.
              </Typography>
            </>
          )}
          {isConnected && (
            <>
              <CheckCircleIcon sx={{ fontSize: 64, color: 'var(--vo-success)' }} />
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'var(--vo-success)' }}>Conexão bem-sucedida!</Typography>
              <Typography sx={{ fontSize: 13, color: T.muted, textAlign: 'center', maxWidth: 400 }}>
                As credenciais foram validadas. O primeiro scan foi iniciado automaticamente.
              </Typography>
              <Button variant="contained" onClick={() => navigate('/scanner/accounts')}
                sx={{ ...BTN, backgroundColor: 'var(--vo-celeste)', color: 'var(--vo-white) !important', '&:hover': { backgroundColor: 'var(--vo-cobalto)' } }}>
                Ver contas
              </Button>
            </>
          )}
          {isFailed && (
            <>
              <ErrorOutlineIcon sx={{ fontSize: 64, color: 'var(--vo-danger)' }} />
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'var(--vo-danger)' }}>Falha na conexão</Typography>
              <Typography sx={{ fontSize: 13, color: T.muted, textAlign: 'center', maxWidth: 440 }}>
                Não foi possível validar as credenciais. Verifique se o Role ARN / Access Key estão corretos e se as permissões foram configuradas.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button variant="outlined" onClick={() => { setValidating(false); setConnStatus(null); }}
                  sx={{ ...BTN, borderColor: T.border, color: T.muted }}>
                  Tentar novamente
                </Button>
                <Button variant="contained" onClick={() => navigate('/scanner/accounts')}
                  sx={{ ...BTN, backgroundColor: 'var(--vo-danger)', color: 'var(--vo-white) !important', '&:hover': { backgroundColor: 'var(--vo-pink-600)' } }}>
                  Sair assim mesmo
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column',
      height: 'calc(100vh - 120px)',
      backgroundColor: 'var(--vo-white)',
      overflow: 'hidden',
    }}>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 16, color: T.text, whiteSpace: 'nowrap' }}>Adding A Provider</Typography>
        {provider && DOCS[provider] && (
          <Button
            variant="text"
            size="sm"
            startIcon={<MenuBookIcon sx={{ fontSize: 15 }} />}
            sx={{ fontSize: 12, textTransform: 'none', color: 'var(--vo-celeste)' }}
            onClick={() => setDocsOpen(true)}
          >
            {docsLabel}
          </Button>
        )}
      </Box>

      {/* ── Body ────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <Sidebar currentStep={step} />

        {/* Content area */}
        <Box sx={{ flex: 1, p: 3, display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto' }}>

            {/* ── Step 0: Select provider ────────────────────────────────── */}
            {step === 0 && (
              <Box>
                <Typography sx={{ fontSize: 13, color: T.muted, mb: 2 }}>
                  Select the cloud provider you want to connect to Virtual Officer.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {PROVIDERS.map(p => (
                    <Box
                      key={p.id}
                      onClick={() => p.available && setProvider(p.id)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 2,
                        p: '14px 16px', borderRadius: 1.5, cursor: p.available ? 'pointer' : 'not-allowed',
                        border: `1px solid ${provider === p.id ? 'var(--vo-celeste)' : T.border}`,
                        backgroundColor: provider === p.id ? 'var(--vo-info-8)' : p.available ? 'var(--vo-white)' : 'var(--vo-mist)',
                        opacity: p.available ? 1 : 0.45,
                        '&:hover': p.available ? { borderColor: 'var(--vo-celeste)', backgroundColor: 'var(--vo-info-8)' } : {},
                        transition: 'all 0.15s',
                      }}
                    >
                      {/* Provider icon badge */}
                      <Box sx={{
                        width: 38, height: 38, borderRadius: 1.5, flexShrink: 0,
                        backgroundColor: p.color, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: p.textColor, letterSpacing: 0.3 }}>
                          {p.initials}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 14, fontWeight: 500, color: T.text, flex: 1 }}>
                        {p.label}
                      </Typography>
                      {!p.available && (
                        <Box sx={{ px: 1, py: 0.3, borderRadius: 1, backgroundColor: 'var(--vo-mist)', border: `1px solid ${T.border}` }}>
                          <Typography sx={{ fontSize: 10, fontWeight: 600, color: T.muted }}>Em breve</Typography>
                        </Box>
                      )}
                      {provider === p.id && (
                        <CheckCircleIcon sx={{ fontSize: 18, color: 'var(--vo-celeste)', flexShrink: 0 }} />
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* ── Step 1 (AWS): Account details ─────────────────────────── */}
            {step === 1 && provider === 'aws' && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: 1, backgroundColor: '#FF9900', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--vo-white)' }}>AWS</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 600, color: T.text }}>Amazon Web Services</Typography>
                </Box>

                <Typography sx={{ fontSize: 12, color: T.muted, mb: 2 }}>
                  Select a method to connect your AWS account.
                </Typography>

                <Box
                  onClick={() => {/* single account - already the only option */}}
                  sx={{
                    p: 2, border: `1px solid var(--vo-celeste)`, borderRadius: 1.5, mb: 1.5,
                    backgroundColor: 'var(--vo-info-8)', cursor: 'default',
                    display: 'flex', alignItems: 'center', gap: 2,
                  }}
                >
                  <Box sx={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid var(--vo-celeste)', backgroundColor: 'var(--vo-celeste)', flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 500, color: T.text }}>Add A Single AWS Cloud Account</Typography>
                </Box>

                <Box sx={{
                  p: 2, border: `1px solid ${T.border}`, borderRadius: 1.5,
                  backgroundColor: 'var(--vo-mist)', opacity: 0.5,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${T.border}`, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 13, color: T.muted }}>Add Multiple Accounts With AWS Organizations</Typography>
                  </Box>
                  <Box sx={{ px: 1, py: 0.3, borderRadius: 1, backgroundColor: 'var(--vo-capri-50)', border: '1px solid var(--vo-capri-100)' }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 600, color: 'var(--vo-cobalto)' }}>Em breve</Typography>
                  </Box>
                </Box>

                <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Account ID*"
                    placeholder="e.g. 123456789012"
                    value={accountId}
                    onChange={e => setAccountId(e.target.value.replace(/\D/g, '').slice(0, 12))}
                    fullWidth
                    inputProps={{ maxLength: 12, inputMode: 'numeric' }}
                  />
                  <TextField
                    label="Provider alias (optional)"
                    placeholder="Enter the provider alias"
                    value={alias}
                    onChange={e => setAlias(e.target.value)}
                    fullWidth
                  />
                </Box>
              </Box>
            )}

            {/* ── Step 2 (AWS): Auth method ─────────────────────────────── */}
            {step === 2 && provider === 'aws' && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: 1, backgroundColor: '#FF9900', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--vo-white)' }}>AWS</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 600, color: T.text }}>Amazon Web Services</Typography>
                </Box>

                <Typography sx={{ fontSize: 12, color: T.muted, mb: 2 }}>Using IAM Role</Typography>

                <Box
                  onClick={() => setAwsMethod('role')}
                  sx={{
                    p: 2, mb: 1.5, borderRadius: 1.5, cursor: 'pointer',
                    border: `1px solid ${awsMethod === 'role' ? 'var(--vo-celeste)' : T.border}`,
                    backgroundColor: awsMethod === 'role' ? 'var(--vo-info-8)' : 'var(--vo-white)',
                    display: 'flex', alignItems: 'center', gap: 2,
                    '&:hover': { borderColor: 'var(--vo-celeste)' },
                  }}
                >
                  <Box sx={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0, border: `2px solid ${awsMethod === 'role' ? 'var(--vo-celeste)' : T.border}`, backgroundColor: awsMethod === 'role' ? 'var(--vo-celeste)' : 'transparent' }} />
                  <Typography sx={{ fontSize: 13, fontWeight: awsMethod === 'role' ? 600 : 400, color: T.text }}>Connect assuming IAM Role</Typography>
                </Box>

                <Typography sx={{ fontSize: 12, color: T.muted, mb: 1 }}>Using Credentials</Typography>

                <Box
                  onClick={() => setAwsMethod('credentials')}
                  sx={{
                    p: 2, borderRadius: 1.5, cursor: 'pointer',
                    border: `1px solid ${awsMethod === 'credentials' ? 'var(--vo-celeste)' : T.border}`,
                    backgroundColor: awsMethod === 'credentials' ? 'var(--vo-info-8)' : 'var(--vo-white)',
                    display: 'flex', alignItems: 'center', gap: 2,
                    '&:hover': { borderColor: 'var(--vo-celeste)' },
                  }}
                >
                  <Box sx={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0, border: `2px solid ${awsMethod === 'credentials' ? 'var(--vo-celeste)' : T.border}`, backgroundColor: awsMethod === 'credentials' ? 'var(--vo-celeste)' : 'transparent' }} />
                  <Typography sx={{ fontSize: 13, fontWeight: awsMethod === 'credentials' ? 600 : 400, color: T.text }}>Connect via Credentials</Typography>
                </Box>
              </Box>
            )}

            {/* ── Step 3 (AWS + IAM Role): Trust policy + Role ARN ────────── */}
            {step === 3 && provider === 'aws' && awsMethod === 'role' && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: 1, backgroundColor: '#FF9900', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--vo-white)' }}>AWS</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 600, color: T.text }}>Connect assuming IAM Role</Typography>
                </Box>

                <Alert severity="info" sx={{ mb: 2, fontSize: 12 }}>
                  Create an IAM Role in your AWS account that trusts the Virtual Officer account, then paste the Role ARN below.
                </Alert>

                <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.text, mb: 0.5 }}>
                  1. Trusted account (Virtual Officer)
                </Typography>
                <CopyBox value={VO_AWS_ACCOUNT_ARN} />

                <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.text, mb: 0.5 }}>
                  2. Trust Policy (paste in AWS IAM → Trust relationships)
                </Typography>
                <Box sx={{ mb: 1.5 }}>
                  <Box sx={{ background: 'var(--vo-mist)', border: `1px solid ${T.border}`, borderRadius: 1.5, p: 1.5 }}>
                    <pre style={{ fontSize: 11, margin: 0, color: T.text, whiteSpace: 'pre-wrap' }}>{iamTrustPolicy}</pre>
                  </Box>
                  <Button size="sm" variant="text" onClick={() => copy(iamTrustPolicy)}
                    startIcon={<ContentCopyIcon sx={{ fontSize: 13 }} />}
                    sx={{ mt: 0.5, height: 28, px: 1, borderRadius: '6px', fontSize: 11, textTransform: 'none', color: T.dim }}>
                    Copiar política
                  </Button>
                </Box>

                <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.text, mb: 0.5 }}>
                  3. ExternalId (already embedded in the policy above)
                </Typography>
                <CopyBox value={externalId} />

                <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.text, mb: 0.5, mt: 1 }}>
                  4. Attach the <strong>SecurityAudit</strong> (AWS managed) policy to the role
                </Typography>

                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Role ARN*"
                    placeholder="arn:aws:iam::123456789012:role/VirtualOfficerScanRole"
                    value={roleArn}
                    onChange={e => setRoleArn(e.target.value)}
                    fullWidth
                    helperText="Cole o ARN da IAM Role criada no passo acima"
                  />
                  <TextField
                    label="Nome da conta"
                    placeholder={alias || `AWS ${accountId}` || 'Minha conta AWS'}
                    value={name}
                    onChange={e => setName(e.target.value)}
                    fullWidth
                    helperText="Nome para identificar esta conta no Virtual Officer"
                  />
                </Box>
              </Box>
            )}

            {/* ── Step 3 (AWS + Credentials): Access Key form ─────────────── */}
            {step === 3 && provider === 'aws' && awsMethod === 'credentials' && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: 1, backgroundColor: '#FF9900', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--vo-white)' }}>AWS</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 600, color: T.text }}>Connect via Credentials</Typography>
                </Box>

                <Alert severity="warning" sx={{ mb: 2.5, fontSize: 12 }}>
                  IAM Roles são mais seguras. Use credenciais estáticas apenas quando não for possível criar uma Role.
                  Nunca use credenciais de root — crie um usuário IAM com a policy <strong>SecurityAudit</strong> (somente leitura).
                </Alert>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Access Key ID*"
                    placeholder="AKIAIOSFODNN7EXAMPLE"
                    value={awsAccessKey}
                    onChange={e => setAwsAccessKey(e.target.value.trim())}
                    fullWidth
                    helperText="Começa com AKIA (long-term) ou ASIA (session)"
                    inputProps={{ spellCheck: false }}
                  />
                  <TextField
                    label="Secret Access Key*"
                    placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
                    value={awsSecretKey}
                    onChange={e => setAwsSecretKey(e.target.value.trim())}
                    fullWidth
                    type={showSecret ? 'text' : 'password'}
                    helperText={
                      <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Chave secreta do usuário IAM</span>
                        <Box component="span" sx={{ cursor: 'pointer', color: 'var(--vo-celeste)', userSelect: 'none' }} onClick={() => setShowSecret(v => !v)}>
                          {showSecret ? 'Ocultar' : 'Mostrar'}
                        </Box>
                      </Box>
                    }
                    inputProps={{ spellCheck: false }}
                  />
                  <TextField
                    label="Session Token (opcional)"
                    placeholder="Apenas para credenciais temporárias (STS)"
                    value={awsSessionToken}
                    onChange={e => setAwsSessionToken(e.target.value.trim())}
                    fullWidth
                    helperText="Necessário somente se o Access Key for temporário (AssumeRole / SSO)"
                    inputProps={{ spellCheck: false }}
                  />
                  <TextField
                    label="Nome da conta"
                    placeholder={alias || `AWS ${accountId}` || 'Minha conta AWS'}
                    value={name}
                    onChange={e => setName(e.target.value)}
                    fullWidth
                    helperText="Nome para identificar esta conta no Virtual Officer"
                  />
                </Box>
              </Box>
            )}

            {/* ── Step 1 (Azure): Credentials ──────────────────────────── */}
            {step === 1 && provider === 'azure' && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: 1, backgroundColor: '#0078D4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--vo-white)' }}>AZ</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 600, color: T.text }}>Microsoft Azure</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField label="Tenant ID*" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" value={azureTenantId} onChange={e => setAzureTenantId(e.target.value)} fullWidth />
                  <TextField label="Client ID (App Registration)*" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" value={azureClientId} onChange={e => setAzureClientId(e.target.value)} fullWidth />
                  <TextField label="Client Secret*" type="password" value={azureClientSecret} onChange={e => setAzureClientSecret(e.target.value)} fullWidth />
                  <TextField label="Nome da conta" placeholder="Minha conta Azure" value={name} onChange={e => setName(e.target.value)} fullWidth helperText="Nome para identificar esta conta no Virtual Officer" />
                </Box>
              </Box>
            )}

            {/* ── Step 2 (Azure): Confirm ───────────────────────────────── */}
            {step === 2 && provider === 'azure' && (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  O primeiro scan será iniciado automaticamente após o cadastro.
                </Alert>
                <Typography sx={{ fontSize: 13, color: T.muted }}>
                  Tenant ID: <strong>{azureTenantId}</strong><br />
                  Client ID: <strong>{azureClientId}</strong>
                </Typography>
              </Box>
            )}

            {/* ── Step 1 (GCP): Service Account ────────────────────────── */}
            {step === 1 && provider === 'gcp' && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: 1, backgroundColor: '#4285F4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--vo-white)' }}>GCP</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 600, color: T.text }}>Google Cloud Platform</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField label="Service Account JSON*" multiline rows={8} value={gcpJson} onChange={e => setGcpJson(e.target.value)} fullWidth placeholder='{"type": "service_account", "project_id": "...", ...}' />
                  <TextField label="Nome da conta" placeholder="Minha conta GCP" value={name} onChange={e => setName(e.target.value)} fullWidth />
                </Box>
              </Box>
            )}

            {/* ── Step 2 (GCP): Confirm ────────────────────────────────── */}
            {step === 2 && provider === 'gcp' && (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  O primeiro scan será iniciado automaticamente após o cadastro.
                </Alert>
              </Box>
            )}

            {/* ── Step 1 (MongoDB): Credentials ────────────────────────── */}
            {step === 1 && provider === 'mongodb' && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: 1, backgroundColor: '#00ED64', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--vo-ink)' }}>MDB</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 600, color: T.text }}>MongoDB Atlas</Typography>
                </Box>
                <Alert severity="info" sx={{ mb: 2.5, fontSize: 12 }}>
                  Crie uma chave de API no MongoDB Atlas com permissão <strong>Organization Read Only</strong> e cole abaixo.
                </Alert>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Organization ID*"
                    placeholder="xxxxxxxxxxxxxxxxxxxxxxxx"
                    value={mongoOrgId}
                    onChange={e => setMongoOrgId(e.target.value)}
                    fullWidth
                    helperText="Encontrado em: MongoDB Atlas → Organization Settings → Organization ID"
                  />
                  <TextField
                    label="Public API Key*"
                    placeholder="abcdefgh"
                    value={mongoPublicKey}
                    onChange={e => setMongoPublicKey(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Private API Key*"
                    type="password"
                    placeholder="••••••••••••••••••••••••••••••••"
                    value={mongoPrivateKey}
                    onChange={e => setMongoPrivateKey(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Nome da conta"
                    placeholder="Minha conta MongoDB Atlas"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    fullWidth
                    helperText="Nome para identificar esta conta no Virtual Officer"
                  />
                </Box>
              </Box>
            )}

            {/* ── Step 2 (MongoDB): Confirm ────────────────────────────── */}
            {step === 2 && provider === 'mongodb' && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: 1, backgroundColor: '#00ED64', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--vo-ink)' }}>MDB</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 600, color: T.text }}>MongoDB Atlas</Typography>
                </Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  O primeiro scan será iniciado automaticamente após o cadastro.
                </Alert>
                <Typography sx={{ fontSize: 13, color: T.muted }}>
                  Organization ID: <strong style={{ color: T.text }}>{mongoOrgId}</strong><br />
                  Public Key: <strong style={{ color: T.text }}>{mongoPublicKey}</strong>
                </Typography>
              </Box>
            )}

        </Box>
      </Box>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <Box sx={{
        px: 3, py: 2,
        borderTop: `1px solid ${T.border}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        backgroundColor: 'var(--vo-mist)',
        flexShrink: 0,
      }}>
        <Button
          variant="secondary"
          onClick={goBack}
          sx={{ ...BTN, borderColor: T.border, color: T.muted }}
        >
          {step === 0 ? 'Cancelar' : 'Voltar'}
        </Button>

        <Box sx={{ flexShrink: 0 }}>
          {!isFinalStep() ? (
            <Button
              variant="primary"
              disabled={!canGoNext()}
              onClick={goNext}
              sx={{
                ...BTN,
                backgroundColor: 'var(--vo-celeste)',
                color: 'var(--vo-white) !important',
                '&:hover': { backgroundColor: 'var(--vo-cobalto)' },
                '&.Mui-disabled': { backgroundColor: 'var(--vo-capri-200)', color: 'var(--vo-white) !important' },
              }}
            >
              Próximo
            </Button>
          ) : (
            <Button
              variant="primary"
              disabled={!canGoNext() || mutation.isLoading}
              onClick={handleSubmit}
              startIcon={mutation.isLoading ? <CircularProgress size={14} color="inherit" /> : null}
              sx={{
                ...BTN,
                backgroundColor: 'var(--vo-celeste)',
                color: 'var(--vo-white) !important',
                '&:hover': { backgroundColor: 'var(--vo-cobalto)' },
                '&.Mui-disabled': { backgroundColor: 'var(--vo-capri-200)', color: 'var(--vo-white) !important' },
              }}
            >
              {mutation.isLoading ? 'Conectando...' : 'Autenticar'}
            </Button>
          )}
        </Box>
      </Box>

      {/* ── Docs Drawer ─────────────────────────────────────────────────── */}
      <DocsDrawer open={docsOpen} onClose={() => setDocsOpen(false)} provider={provider} />
    </Box>
  );
};
